import { useEffect, useRef, useState } from 'react';

type UseAsyncValueOptions<T> = {
	initialValue?: T;
	clearOnChange?: boolean;
};

type AsyncValue<T> = {
	value: T | undefined;
	isLoading: boolean;
	error: unknown;
};

export default function useAsyncValue<T>(factory: () => Promise<T>, deps: React.DependencyList, options?: UseAsyncValueOptions<T>): AsyncValue<T> {
	const { initialValue, clearOnChange = true } = options ?? {};
	const [value, setValue] = useState<T | undefined>(initialValue);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<unknown>();
	const requestIdRef = useRef(0);

	useEffect(() => {
		let cancelled = false;
		const requestId = requestIdRef.current + 1;
		requestIdRef.current = requestId;

		if (clearOnChange) {
			setValue(initialValue);
		}
		setIsLoading(true);
		setError(undefined);

		factory()
			.then((nextValue) => {
				if (!cancelled && requestIdRef.current === requestId) {
					setValue(nextValue);
				}
			})
			.catch((nextError) => {
				if (!cancelled && requestIdRef.current === requestId) {
					setError(nextError);
				}
			})
			.finally(() => {
				if (!cancelled && requestIdRef.current === requestId) {
					setIsLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps);

	return { value, isLoading, error };
}
